import { login as loginGoogle } from 'webtonative/SocialLogin/google';
import { login as loginApple } from 'webtonative/SocialLogin/apple';
import { deviceInfo, isNativeApp } from 'webtonative';

let _wtnCached: boolean | null = null;
// Generous timeout: the native account picker + consent screens can easily take
// tens of seconds. A short timeout drops valid tokens that arrive late.
const WTN_LOGIN_TIMEOUT_MS = 60000;
const WTN_BRIDGE_WAIT_MS = 3000;
const GOOGLE_AUTH_UNAVAILABLE = 'Google sign-in is only available in the app.';
const APPLE_AUTH_UNAVAILABLE = 'Apple sign-in is only available in the iOS app.';

const hasNativeBridge = () =>
	typeof window !== 'undefined' &&
	Boolean(
		isNativeApp ||
		window.WTN?.isIosApp ||
		window.WTN?.isAndroidApp ||
		window.WebToNativeInterface ||
		window.webkit?.messageHandlers?.webToNativeInterface
	);

export function openUrlInDefaultBrowser(url: string): void {
	if (typeof window === 'undefined') return;

	if (hasNativeBridge()) {
		const androidNativeInterface = window.WebToNativeInterface;
		if (typeof androidNativeInterface?.openUrlInBrowser === 'function') {
			androidNativeInterface.openUrlInBrowser(url);
			return;
		}

		if (window.WTN?.isAndroidApp && typeof window.WTN.openUrlInBrowser === 'function') {
			window.WTN.openUrlInBrowser(url);
			return;
		}

		const externalUrl = new URL(url, window.location.href);
		const existingParams = Array.from(externalUrl.searchParams.entries()).filter(
			([key]) => key !== 'loadIn'
		);
		externalUrl.search = '';
		externalUrl.searchParams.set('loadIn', 'defaultBrowser');
		existingParams.forEach(([key, value]) => externalUrl.searchParams.append(key, value));
		window.location.href = externalUrl.toString();
		return;
	}

	window.open(url, '_blank', 'noopener,noreferrer');
}

const normalizeNativePayload = (value: any) => {
	if (typeof value !== 'string') return value;

	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

const getIosNativeHandler = () => {
	if (typeof window === 'undefined') return null;

	return window.webkit?.messageHandlers?.webToNativeInterface ?? null;
};

const getAndroidNativeInterface = () => {
	if (typeof window === 'undefined') return null;

	return window.WebToNativeInterface ?? null;
};

// window.WTN is injected by the native shell and may appear after page load.
// Wait briefly for it instead of failing login attempts made right after startup.
const waitForWindowWTN = (maxWait = WTN_BRIDGE_WAIT_MS) =>
	new Promise((resolve) => {
		if (typeof window === 'undefined') return resolve(false);
		if (window.WTN) return resolve(true);

		let elapsed = 0;
		const interval = 100;
		const timer = setInterval(() => {
			elapsed += interval;
			if (window.WTN) {
				clearInterval(timer);
				resolve(true);
			} else if (elapsed >= maxWait) {
				clearInterval(timer);
				resolve(false);
			}
		}, interval);
	});

export async function isRunningInWTN({ force = false } = {}) {
	if (_wtnCached === true) return true;
	if (_wtnCached === false && !force) return false;

	// Immediate check — official WTN native bridges and window.WTN
	if (typeof window !== 'undefined') {
		if (
			window.WTN ||
			window.WebToNativeInterface ||
			window.webkit?.messageHandlers?.webToNativeInterface
		) {
			_wtnCached = true;
			return true;
		}
	}

	// Poll for the native bridge which may be injected after page load
	const pollForBridge = () =>
		new Promise((resolve) => {
			let elapsed = 0;
			const interval = 100;
			const maxWait = 2000;

			const timer = setInterval(() => {
				elapsed += interval;
				if (
					typeof window !== 'undefined' &&
					(window.WTN ||
						window.WebToNativeInterface ||
						window.webkit?.messageHandlers?.webToNativeInterface)
				) {
					clearInterval(timer);
					resolve(true);
				} else if (elapsed >= maxWait) {
					clearInterval(timer);
					resolve(false);
				}
			}, interval);
		});

	const bridgeFound = await pollForBridge();
	if (bridgeFound) {
		_wtnCached = true;
		return true;
	}

	// Final fallback: try deviceInfo()
	try {
		const info = await deviceInfo();
		_wtnCached = !!info;
		return _wtnCached;
	} catch {
		_wtnCached = false;
		return false;
	}
}

export function wtnGoogleLogin(): Promise<any> {
	return new Promise((resolve) => {
		let isSettled = false;
		let cleanup = () => undefined;
		let timer: number | undefined;

		const finish = (value: any) => {
			if (isSettled) return;
			isSettled = true;
			cleanup();
			window.clearTimeout(timer);
			resolve(normalizeNativePayload(value));
		};

		try {
			timer = window.setTimeout(() => {
				finish({
					isSuccess: false,
					error: 'Google sign-in timed out before the native callback returned.',
					type: 'googleLoginToken',
				});
			}, WTN_LOGIN_TIMEOUT_MS);

			const androidNativeInterface = getAndroidNativeInterface();
			if (androidNativeInterface?.signInWithGoogle) {
				const previousAndroidCBHook = androidNativeInterface.androidCBHook;
				const previousGlobalAndroidCBHook = window.androidCBHook;
				const androidCBHook = (value: any) => {
					const payload = normalizeNativePayload(value);

					if (payload?.type === 'googleLoginToken') {
						finish(payload);
						return;
					}

					previousAndroidCBHook?.(value);
				};

				// Native invokes callbacks via evaluateJavascript on the GLOBAL scope,
				// so register the hook on window too, not just on the injected interface.
				androidNativeInterface.androidCBHook = androidCBHook;
				window.androidCBHook = androidCBHook;
				cleanup = () => {
					if (androidNativeInterface.androidCBHook === androidCBHook) {
						if (previousAndroidCBHook) {
							androidNativeInterface.androidCBHook = previousAndroidCBHook;
						} else {
							delete androidNativeInterface.androidCBHook;
						}
					}
					if (window.androidCBHook === androidCBHook) {
						if (previousGlobalAndroidCBHook) {
							window.androidCBHook = previousGlobalAndroidCBHook;
						} else {
							delete window.androidCBHook;
						}
					}
				};

				androidNativeInterface.signInWithGoogle();
				return;
			}

			const iosNativeHandler = getIosNativeHandler();
			if (iosNativeHandler) {
				const previousIosCBHook = window.iosCBHook;
				const iosCBHook = (value: any) => {
					const payload = normalizeNativePayload(value);

					if (payload?.type === 'googleLoginToken') {
						finish(payload);
						return;
					}

					previousIosCBHook?.(value);
				};

				window.iosCBHook = iosCBHook;
				cleanup = () => {
					if (window.iosCBHook !== iosCBHook) return;

					if (previousIosCBHook) {
						window.iosCBHook = previousIosCBHook;
					} else {
						delete window.iosCBHook;
					}
				};

				iosNativeHandler.postMessage({ action: 'googleSignIn', scope: 'profile email' });
				return;
			}

			waitForWindowWTN().then((bridgeReady) => {
				if (isSettled) return;

				if (!bridgeReady) {
					finish({ isSuccess: false, error: GOOGLE_AUTH_UNAVAILABLE, type: 'googleLoginToken' });
					return;
				}

				try {
					loginGoogle({
						scope: 'profile email',
						callback: finish,
					});
				} catch (err) {
					console.error('WTN Google login error:', err);
					finish({ isSuccess: false, error: String(err), type: 'googleLoginToken' });
				}
			});
		} catch (err) {
			console.error('WTN Google login error:', err);
			finish({ isSuccess: false, error: String(err), type: 'googleLoginToken' });
		}
	});
}

export function wtnAppleLogin(): Promise<any> {
	return new Promise((resolve) => {
		let isSettled = false;
		let cleanup = () => undefined;
		let timer: number | undefined;

		const finish = (value: any) => {
			if (isSettled) return;
			isSettled = true;
			cleanup();
			window.clearTimeout(timer);
			resolve(normalizeNativePayload(value));
		};

		try {
			timer = window.setTimeout(() => {
				finish({
					isSuccess: false,
					error: 'Apple sign-in timed out before the native callback returned.',
					type: 'appleLoginToken',
				});
			}, WTN_LOGIN_TIMEOUT_MS);

			const iosNativeHandler = getIosNativeHandler();

			if (iosNativeHandler) {
				const previousIosCBHook = window.iosCBHook;
				const iosCBHook = (value: any) => {
					const payload = normalizeNativePayload(value);

					if (payload?.type === 'appleLoginToken') {
						finish(payload);
						return;
					}

					previousIosCBHook?.(value);
				};

				window.iosCBHook = iosCBHook;
				cleanup = () => {
					if (window.iosCBHook !== iosCBHook) return;

					if (previousIosCBHook) {
						window.iosCBHook = previousIosCBHook;
					} else {
						delete window.iosCBHook;
					}
				};

				iosNativeHandler.postMessage({ action: 'appleSignIn', scope: 'name email' });
				return;
			}

			waitForWindowWTN().then((bridgeReady) => {
				if (isSettled) return;

				if (!bridgeReady || !window.WTN?.isIosApp) {
					finish({ isSuccess: false, error: APPLE_AUTH_UNAVAILABLE, type: 'appleLoginToken' });
					return;
				}

				try {
					loginApple({
						scope: 'name email',
						callback: finish,
					});
				} catch (err) {
					console.error('WTN Apple login error:', err);
					finish({ isSuccess: false, error: String(err), type: 'appleLoginToken' });
				}
			});
		} catch (err) {
			console.error('WTN Apple login error:', err);
			finish({ isSuccess: false, error: String(err), type: 'appleLoginToken' });
		}
	});
}

/** Documented `deviceInfo()` payload; the SDK itself types it as `any`. */
export interface WtnDeviceInfo {
	platform?: string;
	os?: string;
	osVersion?: string;
	appVersion?: string;
	appVersionCode?: string | number;
	[key: string]: unknown;
}

const DEVICE_INFO_TIMEOUT_MS = 3000;

const isDeviceInfoPayload = (value: unknown): value is WtnDeviceInfo =>
	typeof value === 'object' &&
	value !== null &&
	('appVersion' in value || 'platform' in value || 'os' in value);

const hasDeviceInfoBridge = () =>
	typeof getAndroidNativeInterface()?.getDeviceInfo === 'function' ||
	Boolean(getIosNativeHandler());

// `window.WTN` can precede the Android interface by a few hundred milliseconds;
// give the interface the same grace `waitForWindowWTN` gives the marker.
const waitForDeviceInfoBridge = (maxWait = WTN_BRIDGE_WAIT_MS) =>
	new Promise<boolean>((resolve) => {
		if (typeof window === 'undefined') return resolve(false);
		if (hasDeviceInfoBridge()) return resolve(true);

		let elapsed = 0;
		const interval = 100;
		const timer = setInterval(() => {
			elapsed += interval;
			if (hasDeviceInfoBridge()) {
				clearInterval(timer);
				resolve(true);
			} else if (elapsed >= maxWait) {
				clearInterval(timer);
				resolve(false);
			}
		}, interval);
	});

/**
 * The SDK decides `platform` once at import, so on an Android shell that
 * injects its bridge after the bundle evaluates, `deviceInfo()` rejects with
 * "This function will work in Native App…" even though the bridge is there.
 * This drives the bridge directly, the same way `wtnGoogleLogin` does.
 */
function readDeviceInfoFromBridge(): Promise<WtnDeviceInfo | null> {
	return new Promise((resolve) => {
		let isSettled = false;
		let cleanup = () => undefined;
		let timer: number | undefined;

		const finish = (value: WtnDeviceInfo | null) => {
			if (isSettled) return;
			isSettled = true;
			cleanup();
			window.clearTimeout(timer);
			resolve(value);
		};

		try {
			timer = window.setTimeout(() => finish(null), DEVICE_INFO_TIMEOUT_MS);

			const androidNativeInterface = getAndroidNativeInterface();
			if (typeof androidNativeInterface?.getDeviceInfo === 'function') {
				const previousAndroidCBHook = androidNativeInterface.androidCBHook;
				const previousGlobalAndroidCBHook = window.androidCBHook;
				const androidCBHook = (value: any) => {
					const payload = normalizeNativePayload(value);
					if (isDeviceInfoPayload(payload)) {
						finish(payload);
						return;
					}
					previousAndroidCBHook?.(value);
				};

				// Native invokes callbacks via evaluateJavascript on the GLOBAL scope,
				// so register the hook on window too, not just on the injected interface.
				androidNativeInterface.androidCBHook = androidCBHook;
				window.androidCBHook = androidCBHook;
				cleanup = () => {
					if (androidNativeInterface.androidCBHook === androidCBHook) {
						if (previousAndroidCBHook) {
							androidNativeInterface.androidCBHook = previousAndroidCBHook;
						} else {
							delete androidNativeInterface.androidCBHook;
						}
					}
					if (window.androidCBHook === androidCBHook) {
						if (previousGlobalAndroidCBHook) {
							window.androidCBHook = previousGlobalAndroidCBHook;
						} else {
							delete window.androidCBHook;
						}
					}
				};

				androidNativeInterface.getDeviceInfo();
				return;
			}

			const iosNativeHandler = getIosNativeHandler();
			if (iosNativeHandler) {
				const previousIosCBHook = window.iosCBHook;
				const iosCBHook = (value: any) => {
					const payload = normalizeNativePayload(value);
					if (isDeviceInfoPayload(payload)) {
						finish(payload);
						return;
					}
					previousIosCBHook?.(value);
				};

				window.iosCBHook = iosCBHook;
				cleanup = () => {
					if (window.iosCBHook !== iosCBHook) return;
					if (previousIosCBHook) {
						window.iosCBHook = previousIosCBHook;
					} else {
						delete window.iosCBHook;
					}
				};

				iosNativeHandler.postMessage({ action: 'deviceInfo' });
				return;
			}

			finish(null);
		} catch (err) {
			console.error('WTN deviceInfo bridge error:', err);
			finish(null);
		}
	});
}

/**
 * Installed-app details from the native shell, or null on the website and
 * whenever the shell stays silent. Never throws: callers use this to decide
 * whether to lock the app, and a probe failure must not lock anyone out.
 */
export async function wtnGetDeviceInfo(): Promise<WtnDeviceInfo | null> {
	const isWTN = await isRunningInWTN();
	if (!isWTN) return null;

	try {
		const info = normalizeNativePayload(await deviceInfo());
		if (isDeviceInfoPayload(info)) return info;
	} catch {
		// The SDK snapshotted `platform` before the bridge arrived; ask the bridge directly.
	}

	if (!(await waitForDeviceInfoBridge())) return null;
	return readDeviceInfoFromBridge();
}

export async function wtnGetOS() {
	const info = await wtnGetDeviceInfo();
	return info?.os ?? null;
}
